import { useEffect } from 'react'
import type { FC } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'
import { HeaderGlobalCardSearch } from './HeaderGlobalCardSearch'
import styles from './GlobalSearchSheet.module.css'

type Props = {
  onClose: () => void
}

/**
 * Global search on a phone: the whole screen, opened from the header's search button. It replaces a
 * "Global Search" row that took two extra lines of every page and opened a box whose results hung
 * off the fixed header. MainLayout closes it when a result navigates away.
 */
export const GlobalSearchSheet: FC<Props> = ({ onClose }) => {
  useBodyScrollLock(true)

  useEffect(() => {
    // The search box takes the first Escape to clear itself, and marks it handled.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return createPortal(
    <div className={styles.sheet} role="dialog" aria-modal="true" aria-label="Global search">
      <div className={styles.top}>
        <h2 className={styles.title}>Search</h2>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close search">
          <X size={20} aria-hidden />
        </button>
      </div>
      <HeaderGlobalCardSearch inline />
    </div>,
    document.body,
  )
}
